import { Button, Code, Group, Table, Text } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { CopyableText } from '@/components/ui-shared/CopyableText/CopyableText'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useGetDeviceMetrics,
  useGetEdgeClientVersion
} from '@/queries/deviceQueries'
import { Device } from '@/types/device'
import { CrudAction, Resource } from '@/types/permissions'
import { getRelativeTimeFromNow } from '@/utils/date'
import {
  getDeepStreamVersion,
  getDeviceAttr,
  getDeviceGpus,
  getEdgeClientVersion,
  getLastEdgeClientActionErrors,
  isJetsonDevice
} from '@/utils/device/getDeviceAttr'
import { DeepStreamVersionBadge } from '../DeepStreamVersionBadge/DeepStreamVersionBadge'
import { DeviceTypeBadge } from '../DeviceTypeBadge/DeviceTypeBadge'
import { EdgeClientActionFailureIndicator } from '../EdgeClientActionFailureIndicator/EdgeClientActionFailureIndicator'
import { EdgeClientVersionBadge } from '../EdgeClientVersionBadge/EdgeClientVersionBadge'
import { GpuList } from '../GpuList/GpuList'
import { OnlineStatus } from '../OnlineStatus/OnlineStatus'
import { useEdgeClientActions } from '../useEdgeClientActions'

type DeviceInfoProps = {
  device: Device
  onActionSettled?: () => void
}

export const DeviceInfo = ({ device, onActionSettled }: DeviceInfoProps) => {
  const { data: edgeClientVersionData } = useGetEdgeClientVersion()

  const [canManageDevices] = useHasPermissions(Resource.Devices, [
    CrudAction.Create
  ])

  const { triggerUpdate, triggerRestart, isUpdating, isRestarting } =
    useEdgeClientActions({ deviceId: device.id, onActionSettled })

  const lastConnectTime = getDeviceAttr<number>(device, 'lastConnectTime')
  const lastDisconnectTime = getDeviceAttr<number>(device, 'lastDisconnectTime')

  const { data: uptimeData } = useGetDeviceMetrics(device.id, {
    keys: 'uptime_human',
    limit: 1
  })
  const uptime = uptimeData?.['uptime_human']?.[0]?.value ?? null

  const latestVersion = edgeClientVersionData?.trim()
  const currentVersion = getEdgeClientVersion(device)
  const lastActionErrors = getLastEdgeClientActionErrors(device)

  return (
    <Table
      variant="vertical"
      layout="fixed"
      verticalSpacing="sm"
      horizontalSpacing="md"
      withTableBorder
    >
      <Table.Tbody>
        <Table.Tr>
          <Table.Th w={240}>
            <FormattedMessage id="devices.fields.name" />
          </Table.Th>

          <Table.Td>{device.label}</Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.deviceType" />
          </Table.Th>

          <Table.Td>
            <DeviceTypeBadge isJetson={isJetsonDevice(device)} />
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.id" />
          </Table.Th>

          <Table.Td>
            <CopyableText text={device.id}>
              <Code>{device.id}</Code>
            </CopyableText>
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.code" />
          </Table.Th>

          <Table.Td>
            {/* 'device.name' is VP device identifier */}
            <CopyableText text={device.name}>
              <Code>{device.name}</Code>
            </CopyableText>
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.status" />
          </Table.Th>

          <Table.Td>
            <OnlineStatus status={device.active ? 'online' : 'offline'} />
          </Table.Td>
        </Table.Tr>

        {device.active ? (
          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="devices.fields.onlineSince" />
            </Table.Th>
            <Table.Td>
              {lastConnectTime ? getRelativeTimeFromNow(lastConnectTime) : '-'}
            </Table.Td>
          </Table.Tr>
        ) : (
          <Table.Tr>
            <Table.Th>
              <FormattedMessage id="devices.fields.offlineSince" />
            </Table.Th>
            <Table.Td>
              {lastDisconnectTime
                ? getRelativeTimeFromNow(lastDisconnectTime)
                : '-'}
            </Table.Td>
          </Table.Tr>
        )}

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.uptime" />
          </Table.Th>
          <Table.Td>
            {uptime ?? '-'}
            <Text size="xs" c="dimmed" mt={4}>
              <FormattedMessage id="devices.fields.uptime.hint" />
            </Text>
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.gpus" />
          </Table.Th>

          <Table.Td>
            <GpuList
              gpus={getDeviceGpus(device)}
              isJetson={isJetsonDevice(device)}
            />
            {!isJetsonDevice(device) && getDeviceGpus(device).length > 0 && (
              <Text size="xs" c="dimmed" mt={8}>
                <FormattedMessage id="devices.gpu.restartNotice" />
              </Text>
            )}
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.edgeClient" />
          </Table.Th>

          <Table.Td>
            {(currentVersion || lastActionErrors.update) && (
              <Group wrap="nowrap" gap="xs" align="center">
                <EdgeClientVersionBadge
                  version={currentVersion}
                  latestVersion={latestVersion}
                  isLatest={
                    Boolean(currentVersion) && currentVersion === latestVersion
                  }
                  showInstallButton={canManageDevices && device.active}
                  isDisabled={isRestarting || isUpdating}
                  isLoading={isUpdating}
                  onUpdate={() => void triggerUpdate()}
                />
                {lastActionErrors.update && (
                  <EdgeClientActionFailureIndicator
                    error={lastActionErrors.update}
                  />
                )}
              </Group>
            )}
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="devices.fields.deepStream" />
          </Table.Th>

          <Table.Td>
            <DeepStreamVersionBadge version={getDeepStreamVersion(device)} />
          </Table.Td>
        </Table.Tr>

        <Table.Tr>
          <Table.Td colSpan={2}>
            <Group gap="xs" align="center">
              <Button
                size="xs"
                leftSection={<IconRefresh size={16} />}
                disabled={!device.active || isRestarting || isUpdating}
                loading={isRestarting}
                onClick={() => void triggerRestart()}
              >
                <FormattedMessage id="devices.list.buttons.restart" />
              </Button>
              {lastActionErrors.restart && (
                <EdgeClientActionFailureIndicator
                  error={lastActionErrors.restart}
                />
              )}
            </Group>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  )
}
